import { ProfileRoles } from '@bp/pim-auth-constants';
import { ModalBottomButtons, Select, type SelectOptionType } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import type { MultiValue } from 'react-select';
import { useMutation, useQuery } from 'urql';
import { showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';

const UpdateParentsMutationDocument = graphql(`
  mutation BpUpdateParents($child: ID!, $parents: [ID!]!) {
    updateParents(child: $child, parents: $parents) {
      entitiesUpdated
    }
  }
`);

const ParentProfilesFormPageQueryDocument = graphql(`
  query ParentProfilesFormPageData($childUuid: ID!, $organizationUuid: ID!) {
    listParents(child: $childUuid) {
      __typename
      uuid
    }
    profiles(where: { organization: { uuid: $organizationUuid } }) {
      __typename
      uuid
      organizationRoles
      selectName
    }
  }
`);

type PersonParentFormType = {
  parentUuids: string[];
};

type PersonParentFormProps = {
  childUuid: string;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const PersonParentForm = ({ childUuid, onLoad, onClose }: PersonParentFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [, updateParents] = useMutation(UpdateParentsMutationDocument);

  const [{ data }] = useQuery({
    query: ParentProfilesFormPageQueryDocument,
    variables: {
      childUuid,
      organizationUuid,
    },
    requestPolicy: 'network-only',
  });

  const parentUuids = data?.listParents.map((p) => p.uuid) ?? [];
  const profiles = data?.profiles ?? [];

  const initialValues: PersonParentFormType = { parentUuids };

  const handleSubmit = async (values: PersonParentFormType, helpers: FormikHelpers<PersonParentFormType>) => {
    onLoad(true);

    const { error } = await updateParents({ child: childUuid, parents: values.parentUuids });

    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessUpdateToast();
    }

    onLoad(false);
    helpers.resetForm();
    onClose();
  };

  const parentSelectOpts = useCreateSelectOptions(
    profiles.filter((p) => !p.organizationRoles.includes(ProfileRoles.Student)),
    'uuid',
    'selectName',
  );

  return (
    <Formik<PersonParentFormType> onSubmit={handleSubmit} initialValues={initialValues}>
      {({ resetForm, setFieldValue, values, dirty }) => {
        return (
          <Form>
            <Select
              name='parents'
              label={t('parents.title')}
              value={parentSelectOpts.filter((v) => values.parentUuids.includes(v.value as string))}
              options={parentSelectOpts}
              onChange={async (event) => {
                await setFieldValue(
                  'parentUuids',
                  (event as MultiValue<SelectOptionType>).map((e) => e.value),
                );
              }}
              isMulti
              isSearchable
              menuPosition='fixed'
            />
            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              submitButton={{ disabled: !dirty }}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
